import React from 'react';
import { Database } from 'lucide-react';

function StatCard({ label, value, valueClass, suffix }) {
  return (
    <div className="bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-100">
      <p className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <div className="flex items-end gap-2">
        <span className={`text-xl sm:text-2xl font-black ${valueClass}`}>{value}</span>
        {suffix && <span className="text-[9px] sm:text-[10px] text-gray-500 mb-1">{suffix}</span>}
      </div>
    </div>
  );
}

export function PracticeEvaluation({ year, status, yearlyStats, monthCompletionRate, isMonthComplete, language, t }) {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      <div className="bg-gray-900 p-3 sm:p-4 text-white flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-3">
          <Database size={18} className="text-indigo-400" />
          <h2 className="text-sm sm:text-lg font-bold uppercase tracking-tight">{t.practice.evaluationPanel} {year}</h2>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <div className={`p-4 sm:p-5 rounded-xl border-l-4 ${
          status.color === 'emerald' ? 'bg-emerald-50 border-emerald-500' :
          status.color === 'amber' ? 'bg-amber-50 border-amber-500' :
          'bg-red-50 border-red-500'
        }`}>
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="shrink-0 mt-0.5">{status.icon}</div>
            <div>
              <h3 className={`text-lg sm:text-xl font-black uppercase tracking-tight ${
                status.color === 'emerald' ? 'text-emerald-900' :
                status.color === 'amber' ? 'text-amber-900' :
                'text-red-900'
              }`}>
                {status.title}
              </h3>
              <p className={`text-xs sm:text-sm mt-1 ${
                status.color === 'emerald' ? 'text-emerald-600' :
                status.color === 'amber' ? 'text-amber-600' :
                'text-red-600'
              }`}>
                {status.description}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wider">
            {language === 'es' ? 'CONDICIONES EVALUADAS' : 'EVALUATED CONDITIONS'}
          </h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-100">
              <span className="w-2 h-2 rounded-full bg-red-500 shrink-0"></span>
              <span className="text-xs sm:text-sm text-gray-700">
                {language === 'es' ? 'Completado Mensual' : 'Monthly Completion'}: {monthCompletionRate.toFixed(0)}% / 90%
              </span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-100">
              <span className={`w-2 h-2 rounded-full shrink-0 ${yearlyStats.lastMonthWinRate >= 85 ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
              <span className="text-xs sm:text-sm text-gray-700">
                {language === 'es' ? 'Disciplina' : 'Discipline'} {yearlyStats.lastMonthWinRate >= 85 ? '>= 85%' : '< 85%'}
              </span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-100">
              <span className={`w-2 h-2 rounded-full shrink-0 ${yearlyStats.maxConsecutiveErrors <= 1 ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
              <span className="text-xs sm:text-sm text-gray-700">
                {language === 'es' ? 'Errores Consecutivos' : 'Consecutive Errors'} {yearlyStats.maxConsecutiveErrors <= 1 ? '<= 1' : '> 1'}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <StatCard
            label={`${language === 'es' ? 'AVANCE TOTAL' : 'TOTAL PROGRESS'} (${yearlyStats.lastMonthName || 'N/A'})`}
            value={`${monthCompletionRate.toFixed(0)}%`}
            valueClass={isMonthComplete ? 'text-emerald-600' : monthCompletionRate > 0 ? 'text-amber-600' : 'text-red-600'}
            suffix="META 90%"
          />
          <StatCard
            label={`${language === 'es' ? 'DISCIPLINA' : 'DISCIPLINE'} (${yearlyStats.lastMonthName || 'N/A'})`}
            value={`${yearlyStats.lastMonthWinRate.toFixed(1)}%`}
            valueClass={yearlyStats.lastMonthWinRate >= 90 ? 'text-emerald-600' : yearlyStats.lastMonthWinRate >= 85 ? 'text-amber-600' : 'text-red-600'}
          />
          <div className="bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-100">
            <p className="text-[9px] sm:text-[10px] font-bold text-indigo-500 uppercase tracking-wider mb-1">
              {language === 'es' ? 'DISCIPLINA ANUAL' : 'YEARLY DISCIPLINE'}
            </p>
            <div className="flex items-end gap-2">
              <span className="text-xl sm:text-3xl font-black text-indigo-600">{yearlyStats.yearlyWinRate.toFixed(1)}%</span>
              <span className="text-[9px] sm:text-[10px] text-gray-500 mb-1">
                {language === 'es' ? 'PROMEDIO ANUAL' : 'YEARLY AVERAGE'}
              </span>
            </div>
          </div>
          <StatCard
            label={language === 'es' ? 'TOTAL DÍAS' : 'TOTAL DAYS'}
            value={yearlyStats.totalDays}
            valueClass="text-gray-900"
            suffix={language === 'es' ? 'DÍAS' : 'DAYS'}
          />
          <StatCard
            label={language === 'es' ? 'DÍAS POSITIVOS' : 'POSITIVE DAYS'}
            value={yearlyStats.positiveDays}
            valueClass="text-emerald-600"
            suffix={language === 'es' ? 'DÍAS' : 'DAYS'}
          />
          <StatCard
            label={language === 'es' ? 'DÍAS NEGATIVOS' : 'NEGATIVE DAYS'}
            value={yearlyStats.negativeDays}
            valueClass="text-red-600"
            suffix={language === 'es' ? 'DÍAS' : 'DAYS'}
          />

          <div className="col-span-2 bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-100">
            <p className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              {language === 'es' ? 'BALANCE DE DÍAS ANUAL' : 'YEARLY DAY BALANCE'}
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden flex">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${yearlyStats.totalDays > 0 ? (yearlyStats.positiveDays / yearlyStats.totalDays) * 100 : 0}%` }}
                />
                <div
                  className="h-full bg-red-500 transition-all"
                  style={{ width: `${yearlyStats.totalDays > 0 ? (yearlyStats.negativeDays / yearlyStats.totalDays) * 100 : 0}%` }}
                />
              </div>
            </div>
            <div className="flex justify-between text-[10px] sm:text-xs mt-2 font-bold">
              <span className="text-emerald-600">{yearlyStats.positiveDays} POS</span>
              <span className="text-red-600">{yearlyStats.negativeDays} NEG</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
